import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Paper, LinearProgress, Alert, CircularProgress,
  IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MergeIcon from '@mui/icons-material/MergeType';
import DeleteIcon from '@mui/icons-material/Delete';
import { listProjects, createProject, deleteProject } from '../../api/annotatorProjects';
import type { AnnotatorProject } from '../../types/annotator';

export default function AnnotatorProjectListPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<AnnotatorProject[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<AnnotatorProject | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const data = await listProjects();
        setProjects([...data].sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const p = await createProject({ name, description, organisation_id: 1 });
      setProjects((prev) => [...prev, p].sort((a, b) => a.name.localeCompare(b.name)));
      setName('');
      setDescription('');
      setShowForm(false);
    } catch {
      setError('Failed to create project.');
    }
  }

  function openDeleteDialog(project: AnnotatorProject, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(project);
    setDeleteConfirmName('');
    setDeleteError('');
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmName('');
    setDeleteError('');
  }

  async function handleDelete() {
    if (!deleteTarget || deleteConfirmName !== deleteTarget.name) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteProject(deleteTarget.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setDeleteError('Failed to delete project. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  const confirmMatches = deleteTarget !== null && deleteConfirmName === deleteTarget.name;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>Annotation Projects</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<MergeIcon />} onClick={() => navigate('/annotator/combine')}>
            Combine Projects
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'New Project'}
          </Button>
        </Box>
      </Box>

      {showForm && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <form onSubmit={handleCreate}>
            <Typography variant="h6" sx={{ mb: 2 }}>New Project</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus size="small" />
              <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} size="small" />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" sx={{ alignSelf: 'flex-start' }}>Create</Button>
            </Box>
          </form>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : projects.length === 0 ? (
        <Typography color="text.secondary">No projects yet. Create one to get started.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {projects.map((p) => {
            const pct = p.image_count > 0 ? (p.annotated_count / p.image_count) * 100 : 0;
            return (
              <Paper
                key={p.id}
                component={RouterLink}
                to={`/annotator/projects/${p.id}`}
                sx={{
                  p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  textDecoration: 'none', color: 'inherit',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>{p.name}</Typography>
                  {p.description && <Typography variant="body2" color="text.secondary">{p.description}</Typography>}
                </Box>
                <Box sx={{ minWidth: 200, textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {p.annotated_count} / {p.image_count} annotated
                  </Typography>
                  <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => openDeleteDialog(p, e)}
                  sx={{ ml: 1, flexShrink: 0 }}
                  aria-label={`Delete project ${p.name}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={deleteTarget !== null} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently delete <strong>{deleteTarget?.name}</strong> and all of its
            images, classes, and annotations. This cannot be undone.
          </DialogContentText>
          <TextField
            label={`Type "${deleteTarget?.name}" to confirm`}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && confirmMatches) handleDelete(); }}
          />
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!confirmMatches || deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting…' : 'Delete Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
